import { Component, OnInit } from '@angular/core';
import { AppServices } from '../../services/appServices';
import { practiceSessionTemplateResponse } from '../../models/practiceSessionTemplateResponse';
import { NgFor, NgIf, DatePipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { result } from '../../models/result';
import { practiceTemplateDrillResponse } from '../../models/practiceTemplateDrillResponse';
import { savePracticeSessionDrillResultsRequest } from '../../models/savePracticeSessionDrillResultsRequest';
import { savePracticeSessionRequest } from '../../models/savePracticeSessionRequest';
import { practiceSessionSearchResponse } from '../../models/practiceSessionSearchResponse';
import { searchPracticeSessionsRequest } from '../../models/searchPracticeSessionsRequest';
import { TitleStrategy, ɵEmptyOutletComponent } from '@angular/router';
import { searchPracticeSessionDrillResultsRequest } from '../../models/searchPracticeSessionDrillResultsRequest';
import { practiceSessionDrillResultSearchResponse } from '../../models/practiceSessionDrillResultSearchResponse';

@Component({
  selector: 'app-practice-session',
  standalone: true,
  imports: [NgIf, NgFor, FormsModule, DatePipe, ɵEmptyOutletComponent, NgClass],
  templateUrl: './practice-session.component.html',
  styleUrl: './practice-session.component.css'
})
export class PracticeSessionComponent implements OnInit {
  constructor(private appServices:AppServices){}

  ngOnInit(): void {
    this.searchTemplates();
    this.searchResults();
    this.searchPracticeSessions();
  }

  templates:practiceSessionTemplateResponse[] = [];
  resultOptions:result[] = [];
  practiceSessions:practiceSessionSearchResponse[] = [];
  selectedTemplate:practiceSessionTemplateResponse|null = null;
  practiceSessionId:number|null = null;
  practiceSessionDrillResults:Array<practiceSessionDrillResultSearchResponse> = [];

  get sortedResultOptions() {
    return [...this.resultOptions].sort((a, b) => b.sort_order - a.sort_order);
  }

  async searchTemplates() {
    let username = this.getUsername();
    if(!username) return;

    this.appServices.applicationService.showGlobalSpinner();
    this.templates = await this.appServices.templateService.searchTemplates(username);
    this.appServices.applicationService.hideGlobalSpinner();
  }

  async searchResults() {
    this.appServices.applicationService.showGlobalSpinner();
    this.resultOptions = await this.appServices.lookupService.searchResults();
    this.appServices.applicationService.hideGlobalSpinner();
  }

  async startPracticeSession() {
    let username = this.getUsername();
    if(!username || !this.selectedTemplate) return;

    this.appServices.applicationService.showGlobalSpinner();
    let dateKey = this.appServices.dataMappingService.getDateKey();
    let req:savePracticeSessionRequest = {
      session_date: new Date().toISOString(),
      username: username,
      is_deleted: false,
      practice_session_template_id: this.selectedTemplate.id,
      date_key: dateKey
    };

    this.practiceSessionId = await this.appServices.practiceService.createPracticeSession(req);
    this.appServices.applicationService.hideGlobalSpinner();
  }

  async searchPracticeSessions() {
    let username = this.getUsername();
    if(!username) return;

    this.appServices.applicationService.showGlobalSpinner();
    let req:searchPracticeSessionsRequest = {
      username: username,
      pageNumber: 1,
      pageSize: 100
    };

    this.practiceSessions = await this.appServices.practiceService.searchPracticeSessions(req);
    this.appServices.applicationService.hideGlobalSpinner();
  }

  getUsername():string|null {
    let username = this.appServices.authenticationService.loggedInUser?.username;
    if(username) return username;
    return null;
  }

  currentDrillIndex: number = 0;
  currentRepIndex: number = 0;

  get templateDrills() {
    return this.selectedTemplate?.template_drills ?? [];
  }

  get currentTemplateDrill() {
    if (!this.templateDrills.length) {
      return null;
    }

    if (this.currentDrillIndex < 0) {
      this.currentDrillIndex = 0;
    }

    if (this.currentDrillIndex >= this.templateDrills.length) {
      this.currentDrillIndex = this.templateDrills.length - 1;
    }

    return this.templateDrills[this.currentDrillIndex];
  }

  get currentRepNumber(): number {
    return this.currentRepIndex + 1;
  }

  get isFirstDrill(): boolean {
    return this.currentDrillIndex === 0;
  }

  get isLastDrill(): boolean {
    return this.currentDrillIndex === this.templateDrills.length - 1;
  }

  get isFirstRep(): boolean {
    return this.currentRepIndex === 0;
  }

  get isLastRep(): boolean {
    const reps = this.currentTemplateDrill?.number_of_reps ?? 0;
    return this.currentRepIndex === reps - 1;
  }

  moveToNextDrill(): void {
    if (this.currentDrillIndex < this.templateDrills.length - 1) {
      this.currentDrillIndex++;
      this.currentRepIndex = 0;
    }
  }

  moveToPreviousDrill(): void {
    if (this.currentDrillIndex > 0) {
      this.currentDrillIndex--;
      this.currentRepIndex = 0;
    }
  }

  moveToNextRep(): void {
    const reps = this.currentTemplateDrill?.number_of_reps ?? 0;

    if (this.currentRepIndex < reps - 1) {
      this.currentRepIndex++;
    } else {
      this.moveToNextDrill();
    }
  }

  moveToPreviousRep(): void {
    if (this.currentRepIndex > 0) {
      this.currentRepIndex--;
    } else if (this.currentDrillIndex > 0) {
      this.currentDrillIndex--;
      const previousDrill = this.currentTemplateDrill;
      this.currentRepIndex = Math.max((previousDrill?.number_of_reps ?? 1) - 1, 0);
    }
  }

  setSelectedTemplate(template: any): void {
    this.selectedTemplate = template;
    this.currentDrillIndex = 0;
    this.currentRepIndex = 0;
  }

  async selectResult(result: result): Promise<void> {
    console.log('selected result', {
      drillIndex: this.currentDrillIndex,
      repIndex: this.currentRepIndex,
      result: result
    });

    if(!this.currentTemplateDrill || !this.practiceSessionId) return;

    let req:savePracticeSessionDrillResultsRequest = {
      results: []
    };

    req.results.push({
      drill_id: this.currentTemplateDrill?.drill_id,
      practice_session_id: this.practiceSessionId,
      rep_number: this.currentRepIndex + 1,
      result_id: result.id,
      is_deleted: false
    });

    this.appServices.practiceService.savePracticeSessionDrillResult(req)
    this.practiceSessionDrillResults.push({
      id:0,
      drill_id: this.currentTemplateDrill?.drill_id,
      practice_session_id: this.practiceSessionId,
      is_deleted: false,
      rep_number: this.currentRepIndex + 1,
      result_id: result.id
    });
    this.moveToNextRep();
  }

  async selectPracticeSession(session:practiceSessionSearchResponse) {
    this.practiceSessionId = session.id;
    let foundTemplate = this.templates.filter(t => t.id === session.practice_session_template_id);
    if(foundTemplate.length > 0) {
      this.appServices.applicationService.showGlobalSpinner();
      this.selectedTemplate = foundTemplate[0];
      this.practiceSessionDrillResults = await this.appServices.practiceService.searchPracticeSessionDrillResults({practice_session_id: this.practiceSessionId});
      this.appServices.applicationService.hideGlobalSpinner();
    }
  }

  isPracticeResultSelected(practiceSessionId:number, drillId:number, resultId:number, repNumber:number):boolean {
    let result = this.practiceSessionDrillResults.filter(dr => dr.practice_session_id === practiceSessionId && dr.drill_id === drillId && dr.result_id === resultId && dr.rep_number === repNumber);
    if(!result || result.length === 0) return false;
    return true;
  }
}